import { APITypesV1 } from "@cur8/api-client";
import {
  BottomSheet,
  Button,
  Icon16Capture,
  Icon16Close,
  Icon16Edit,
  Icon24Checkmark,
  Icon24Close,
  Icon24Minus,
  IconButton,
  Typography,
} from "@cur8/maneki";
import { useQueryClient } from "@tanstack/react-query";
import {
  TubeDrawDecision,
  useConfirmDrawMutation,
} from "hooks/mutations/useConfirmDrawMutation";
import { useLabStatusBloodSamplesQuery } from "hooks/queries/useLabStatusBloodSamplesQuery";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { formatDate } from "lib/formatDateTime";
import { tubeCapColour } from "lib/tubeCapColour";
import {
  isRejected,
  labPillFor,
  LabProgress,
  labProgressFor,
  orderedTubes,
  resolutionFor,
  sampleTypeLabels,
  TubeResolution,
} from "lib/tubeStatus";
import { CheckInParams } from "lib/types/checkInParameters";
import {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Checkbox } from "react-aria-components";
import { useFeaturePreference } from "render/hooks/useFeaturePreference";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent/PageFrameContent";
import { Banner, BannerKind } from "render/ui/presentation/Banner";
import { BarcodeScanner } from "render/ui/presentation/BarcodeScanner";
import { Group } from "render/ui/presentation/Group";
import { RadioGroup } from "render/ui/presentation/RadioGroup";
import { StatusBadge } from "render/ui/presentation/StatusBadge";
import { TubeCap } from "render/ui/symbol/TubeCap";
import { Link } from "render/ui/trigger/Link";
import { RetryScanButton } from "render/ui/trigger/RetryScanButton";
import { drawSubmitErrorMessage } from "./errorMessage";
import styles from "./styles.module.sass";

type DrawConfirmationViewProps = {
  goTo(url: string): void;
  // Where the left footer button leads, supplied per entry point: Back for the
  // scan progress list, forward for the step, whose Back is the skin scanner.
  exit: { label: string; onPress(): void };
  // Where "Done" CTA leads once the blood draw is confirmed, supplied per entry point
  doneUrl: string;
  // Only the blood-draw step after the skin scanner holds scanning behind a
  // tap, so the camera isn't reading the moment the screen opens in front of
  // the member. The scan-progress entry opens straight into scanning.
  requireScanStart?: boolean;
} & CheckInParams;

const SKIP_REASONS: APITypesV1.SkipReason[] = [
  APITypesV1.SkipReason.PatientDeclined,
  APITypesV1.SkipReason.PatientUnwell,
  APITypesV1.SkipReason.TechnicalIssue,
  APITypesV1.SkipReason.Other,
];

const SKIP_REASON_LABELS: Record<APITypesV1.SkipReason, string> = {
  [APITypesV1.SkipReason.PatientDeclined]: "Member declined",
  [APITypesV1.SkipReason.PatientUnwell]: "Member unwell",
  [APITypesV1.SkipReason.TechnicalIssue]: "Technical issue",
  [APITypesV1.SkipReason.Other]: "Other",
};

type PendingDecision =
  | { decision: "drawn" }
  | { decision: "skipped"; reason: APITypesV1.SkipReason };

// Length of the scanner collapse — keep in sync with the transition on
// `.scanCollapse` in styles.module.sass.
const SCAN_COLLAPSE_MS = 300;

const MANUAL_MODE_LABEL = "Unable to scan? Confirm manually";
const SCAN_MODE_LABEL = "Use the scanner instead";

// Says where the pills come from, so "received" doesn't read as a verdict this
// screen reached.
const LAB_PROGRESS_COPY: Record<LabProgress, string> = {
  awaitingLab: "Each tube's status appears once the lab receives it.",
  labWorking: "The lab updates each tube's status as it works through them.",
  labFinished: "Every tube has its final status from the lab.",
};

const IDENTITY_COPY = "I confirm the information on the label matches:";
// The statement these replaced, kept verbatim for when a value is missing.
const IDENTITY_COPY_WITHOUT_VALUES =
  "I confirm label matches member name and date of birth";

// Review wording agrees with the "Review tubes" link that leads here: nothing
// left to confirm, no identity check to run. One table, so the two modes can't
// half-apply.
const SCREEN_COPY = {
  confirm: {
    title: "Confirm tubes",
    subtitle:
      "Confirm the member's identity against the label before the tube leaves the room.",
  },
  review: {
    title: "Review tubes",
    subtitle: "Every tube for this member is recorded.",
  },
};

export function DrawConfirmationView({
  goTo,
  exit,
  doneUrl,
  patientId,
  visitId,
  requireScanStart = false,
}: DrawConfirmationViewProps) {
  const { data: featurePreferences } = useFeaturePreference();
  // Off: Done waits for every tube and sends them in one bulk request, exactly
  // as it did before AB#91601.
  const singleVialConfirmEnabled = featurePreferences.singleVialConfirmEnabled;
  // The start gate rides on the blood-draw step flag: with it off the step
  // isn't in the flow, so the scanner behaves exactly as before.
  const bloodDrawStepEnabled = featurePreferences.bloodDrawStepEnabled;
  const { data: patient } = usePatientQuery({ patientId });
  const { data: visit } = useVisitQuery({ patientId, visitId });
  const countryCode = visit?.site.address.countryCode?.toUpperCase();
  const { labStatusEnabled, query: bloodSamplesQuery } =
    useLabStatusBloodSamplesQuery({ patientId, visitId });
  const labProgress = labProgressFor(bloodSamplesQuery.data?.bloodSamples);
  const samplesLoaded = bloodSamplesQuery.data != null;
  const tubes = useMemo(
    () => orderedTubes(bloodSamplesQuery.data?.bloodSamples),
    [bloodSamplesQuery.data]
  );

  const queryClient = useQueryClient();
  const confirmDrawMutation = useConfirmDrawMutation();

  // Freely toggleable, local-only until Done is pressed — the backend has no
  // way to reverse an already-confirmed tube, so nothing is sent per tap.
  const [pendingDecisions, setPendingDecisions] = useState<
    Record<string, PendingDecision>
  >({});
  const [skipTarget, setSkipTarget] = useState<APITypesV1.BloodSample | null>(
    null
  );
  const [skipReason, setSkipReason] = useState<APITypesV1.SkipReason | "">("");
  // retry: omitted = no Scan new label button; "rescan" clears the dedupe;
  // "restart" also re-acquires the camera (only for a dead stream).
  const [error, setError] = useState<
    | {
        message: string;
        sampleId?: string;
        kind?: BannerKind;
        retry?: "rescan" | "restart";
      }
    | undefined
  >();

  // Tubes matched by a scan this session. Resolved tubes stay revealed too, so
  // a partially-done visit doesn't hide itself behind a re-scan.
  const [scannedIds, setScannedIds] = useState<Record<string, true>>({});
  const [labelAttachedIds, setLabelAttachedIds] = useState<
    Record<string, true>
  >({});
  // Manual mode's counterpart to a scan: nothing opens until the operator
  // starts it, so they work one tube at a time either way.
  const [startedIds, setStartedIds] = useState<Record<string, true>>({});
  const [manualMode, setManualMode] = useState(false);
  // Held here, not in BarcodeScanner: toggling to manual entry unmounts the
  // scanner, and this tap should last the whole session rather than be asked
  // for again when the operator switches back to scanning.
  const [scanStarted, setScanStarted] = useState(false);
  const [scanToken, setScanToken] = useState(0);
  const [retryToken, setRetryToken] = useState(0);
  const [restartToken, setRestartToken] = useState(0);
  const rearm = useCallback(() => setScanToken((n) => n + 1), []);

  // Folded away when a scan opens a card or raises an error, to give whichever
  // one the operator has to read the room. A scan that reveals nothing leaves
  // it up.
  const [scanExpanded, setScanExpanded] = useState(true);

  const handleRetry = useCallback(() => {
    setRetryToken((n) => n + 1);
    // Only re-acquire the camera for a dead stream; a scan mismatch leaves the
    // camera live, so clearing the dedupe is enough.
    if (error?.retry === "restart") {
      setRestartToken((n) => n + 1);
    }
    setError(undefined);
    setScanExpanded(true);
  }, [error]);

  // Already submitted in a prior session — the backend can't reverse this,
  // so it's shown read-only regardless of local pending state.
  const isLocked = useCallback(
    (sample: APITypesV1.BloodSample) => resolutionFor(sample) !== "pending",
    []
  );

  const isRevealed = useCallback(
    (sample: APITypesV1.BloodSample) =>
      scannedIds[sample.bloodSampleId] === true ||
      startedIds[sample.bloodSampleId] === true ||
      isLocked(sample) ||
      pendingDecisions[sample.bloodSampleId] != null,
    [scannedIds, startedIds, isLocked, pendingDecisions]
  );

  // Both boxes ticked this session. The Done gate and the drawn count share
  // this definition so they can't drift apart.
  const isDrawnNow = useCallback(
    (tube: APITypesV1.BloodSample) =>
      pendingDecisions[tube.bloodSampleId]?.decision === "drawn" &&
      labelAttachedIds[tube.bloodSampleId] === true,
    [pendingDecisions, labelAttachedIds]
  );

  // A tube the operator is drawing now needs both of its checkboxes; a skipped
  // tube and one recorded in a prior session have no checkboxes to tick.
  const isTubeResolved = useCallback(
    (tube: APITypesV1.BloodSample) =>
      isLocked(tube) ||
      pendingDecisions[tube.bloodSampleId]?.decision === "skipped" ||
      isDrawnNow(tube),
    [isLocked, pendingDecisions, isDrawnNow]
  );
  const resolvedCount = tubes.filter(isTubeResolved).length;
  // What Done would send: a tube decided this session, whose decision is
  // complete. A half-ticked tube is left open, the same pause an untouched tube
  // gets, so it can never be sent as drawn on one box alone.
  const decidedCount = tubes.filter(
    (tube) => !isLocked(tube) && isTubeResolved(tube)
  ).length;
  const hasDecisionsToSubmit = decidedCount > 0;
  const allResolved = tubes.length > 0 && resolvedCount === tubes.length;
  // Every tube was settled before this screen was opened: there is nothing to
  // do here and Done has nothing to send, so say so rather than leaving the
  // operator with a dead button.
  const nothingLeftToRecord =
    samplesLoaded && allResolved && !hasDecisionsToSubmit;
  // Every screen change this story makes hangs off this, so with the flag off
  // the screen is exactly what it was before (AB#92441).
  const reviewMode = labStatusEnabled && nothingLeftToRecord;
  const copy = SCREEN_COPY[reviewMode ? "review" : "confirm"];
  // Done can leave tubes behind now, so it counts where pressing it leaves the
  // member's set: the tally Scan progress then shows, tubes from earlier in the
  // visit included. With nothing to send there is no claim to make, as with the
  // flag off, where Done always takes the whole set.
  const doneLabel =
    singleVialConfirmEnabled && hasDecisionsToSubmit
      ? `Done (${resolvedCount}/${tubes.length})`
      : "Done";

  const isDrawn = useCallback(
    (tube: APITypesV1.BloodSample) =>
      isLocked(tube) ? resolutionFor(tube) === "drawn" : isDrawnNow(tube),
    [isLocked, isDrawnNow]
  );
  const drawnCount = tubes.filter(isDrawn).length;
  // The heading counts the same tubes Done and Scan progress do, or the screen
  // shows two different n/2 at once: draw one tube and skip the other and a
  // drawn-only heading reads 1/2 under a Done (2/2). Flag off it stays the
  // count of tubes drawn, exactly as before.
  const headingCount = singleVialConfirmEnabled ? resolvedCount : drawnCount;
  const allRevealed = tubes.length > 0 && tubes.every(isRevealed);
  // Retry applies only to scan errors, so offer it only while the scanner is
  // shown (a submit error has no scanner to reset).
  const showScanner = !manualMode && !allRevealed && samplesLoaded;
  const scanOpen = showScanner && scanExpanded;

  // Finishing a tube means the next one is up, so bring the scanner back
  // without making the operator ask. Only on a rise: undoing a tube shouldn't
  // reopen it. If that was the last tube, showScanner keeps it down anyway.
  const resolvedCountRef = useRef(resolvedCount);
  useEffect(
    function reopenScannerWhenATubeIsFinished() {
      const previous = resolvedCountRef.current;
      resolvedCountRef.current = resolvedCount;
      if (resolvedCount > previous) {
        setScanExpanded(true);
      }
    },
    [resolvedCount]
  );

  // The scanner is `display: none` while inactive, so the camera has to stay up
  // for the length of the collapse — otherwise the viewport blinks out instead
  // of folding away. Released once it's out of sight.
  const [cameraActive, setCameraActive] = useState(true);
  useEffect(
    function releaseCameraAfterCollapse() {
      if (scanOpen) {
        setCameraActive(true);
        return;
      }
      const timeoutId = window.setTimeout(
        () => setCameraActive(false),
        SCAN_COLLAPSE_MS
      );
      return () => window.clearTimeout(timeoutId);
    },
    [scanOpen]
  );

  const handleScan = useCallback(
    (scannedCode: string) => {
      const match = tubes.find((tube) => tube.bloodSampleId === scannedCode);
      if (!match) {
        setScanExpanded(false);
        setError({
          message: "Scanned barcode doesn't match any tube for this visit:",
          sampleId: scannedCode,
          retry: "rescan",
        });
        rearm();
        return;
      }
      if (isRevealed(match)) {
        // Nothing to open: this tube is already dealt with. Stay up so the
        // next tube can go straight in front of the camera.
        setError({
          message: `${sampleTypeLabels[match.sampleType]} has already been scanned. Scan the next label.`,
          kind: "caution",
        });
        rearm();
        return;
      }
      setScanExpanded(false);
      setError(undefined);
      setScannedIds((prev) => ({ ...prev, [match.bloodSampleId]: true }));
      rearm();
    },
    [tubes, rearm, isRevealed]
  );

  const handleScanError = useCallback(
    (err: Error) => {
      setScanExpanded(false);
      setError({ message: `Camera error: ${err.message}`, retry: "restart" });
      rearm();
    },
    [rearm]
  );

  const enableManualMode = useCallback(() => {
    setError(undefined);
    setManualMode(true);
  }, []);

  const enableScanMode = useCallback(() => {
    setError(undefined);
    setManualMode(false);
    setScanExpanded(true);
    // Back to the scanner being the gate: undecided tubes close again.
    setStartedIds({});
  }, []);

  const startTube = useCallback((bloodSampleId: string) => {
    setStartedIds((prev) => ({ ...prev, [bloodSampleId]: true }));
  }, []);

  const toggleConfirm = useCallback(
    (bloodSampleId: string, checked: boolean) => {
      setPendingDecisions((prev) => {
        if (!checked) {
          const next = { ...prev };
          delete next[bloodSampleId];
          return next;
        }
        return { ...prev, [bloodSampleId]: { decision: "drawn" } };
      });
    },
    []
  );

  // Held here rather than in TubeCard: flipping back to scan mode unmounts
  // unresolved cards, and this tick shouldn't evaporate with them.
  const toggleLabelAttached = useCallback(
    (bloodSampleId: string, checked: boolean) => {
      setLabelAttachedIds((prev) => {
        if (!checked) {
          const next = { ...prev };
          delete next[bloodSampleId];
          return next;
        }
        return { ...prev, [bloodSampleId]: true };
      });
    },
    []
  );

  const undoTube = useCallback((bloodSampleId: string) => {
    setPendingDecisions((prev) => {
      const next = { ...prev };
      delete next[bloodSampleId];
      return next;
    });
    setLabelAttachedIds((prev) => {
      const next = { ...prev };
      delete next[bloodSampleId];
      return next;
    });
  }, []);

  const openSkip = useCallback((sample: APITypesV1.BloodSample) => {
    setSkipReason("");
    setSkipTarget(sample);
  }, []);

  const closeSkip = useCallback(() => {
    setSkipTarget(null);
    setSkipReason("");
  }, []);

  const confirmSkip = useCallback(() => {
    if (!skipTarget || !skipReason) {
      return;
    }
    setPendingDecisions((prev) => ({
      ...prev,
      [skipTarget.bloodSampleId]: { decision: "skipped", reason: skipReason },
    }));
    closeSkip();
  }, [closeSkip, skipReason, skipTarget]);

  const handleDone = useCallback(() => {
    // Only complete decisions travel. A tube left half-ticked, or untouched, is
    // simply not in this batch and stays open for later in the visit.
    const decisions = tubes.flatMap<TubeDrawDecision>((tube) => {
      if (isLocked(tube)) {
        return [];
      }
      const decision = pendingDecisions[tube.bloodSampleId];
      if (decision?.decision === "skipped") {
        return [
          {
            bloodSampleId: tube.bloodSampleId,
            decision: "skipped",
            reason: decision.reason,
          },
        ];
      }
      return isDrawnNow(tube)
        ? [{ bloodSampleId: tube.bloodSampleId, decision: "drawn" }]
        : [];
    });
    if (decisions.length === 0) {
      return;
    }

    setError(undefined);
    confirmDrawMutation.mutate(
      { patientId, decisions },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["visitBloodSamples", patientId, visitId],
          });
          goTo(doneUrl);
        },
        onError: (err: unknown) => {
          // Submit error, not a scan error: no retry (omit retry).
          setError({ message: drawSubmitErrorMessage(err, tubes) });
        },
        // Also on failure: with one call per tube, some of the batch can have
        // landed, and those tubes have to come back locked rather than look
        // like they still need doing.
        onSettled: () => {
          queryClient.invalidateQueries({
            queryKey: ["visitBloodSamples", patientId, visitId],
          });
        },
      }
    );
  }, [
    confirmDrawMutation,
    doneUrl,
    goTo,
    isDrawnNow,
    isLocked,
    patientId,
    pendingDecisions,
    queryClient,
    tubes,
    visitId,
  ]);

  // The two mode switches share one slot under the scanner, so whichever
  // applies always sits in the same place.
  let modeSwitch: { label: string; onPress(): void } | undefined;
  if (manualMode) {
    if (!allResolved) {
      modeSwitch = { label: SCAN_MODE_LABEL, onPress: enableScanMode };
    }
  } else if (showScanner) {
    modeSwitch = { label: MANUAL_MODE_LABEL, onPress: enableManualMode };
  }

  // Named in full for the identity check: displayName can hold a preferred
  // name, which is not what the tube label carries.
  const legalName =
    [patient?.name?.firstName, patient?.name?.lastName]
      .filter(Boolean)
      .join(" ") || undefined;
  // The box is context, not a check, so it can fall back further.
  const fullName = legalName ?? patient?.name?.displayName ?? "the member";
  const dateOfBirthLabel = patient?.dateOfBirth
    ? formatDate(patient.dateOfBirth, countryCode)
    : undefined;

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout} data-lab-status={labStatusEnabled}>
            <Typography variant="display-s">{copy.title}</Typography>
            <Typography variant="body-m" color="subtle">
              {copy.subtitle}
            </Typography>
            {!manualMode && samplesLoaded ? (
              <div
                className={`${styles.collapse} ${styles.scanCollapse}`}
                data-open={scanOpen}
                aria-hidden={!scanOpen}
              >
                <div className={styles.collapseInner}>
                  <div className={styles.scanSection}>
                    <BarcodeScanner
                      onScan={handleScan}
                      onError={handleScanError}
                      active={cameraActive}
                      resetToken={scanToken}
                      retryToken={retryToken}
                      restartToken={restartToken}
                      requireStart={requireScanStart && bloodDrawStepEnabled}
                      startLabel="Begin scanning tubes"
                      started={scanStarted}
                      onStart={() => setScanStarted(true)}
                    />
                  </div>
                </div>
              </div>
            ) : undefined}
            {/* Under the scanner, where the scanning is failing. Hidden rather
                than unmounted so switching modes doesn't shift the cards. */}
            {nothingLeftToRecord ? undefined : (
              <div
                className={styles.scanModeSlot}
                data-visible={modeSwitch != null}
                aria-hidden={modeSwitch == null}
              >
                <Link
                  onPress={modeSwitch?.onPress ?? enableManualMode}
                  className={`${styles.secondaryLink} ${styles.scanModeLink}`}
                >
                  {modeSwitch?.label ?? MANUAL_MODE_LABEL}
                </Link>
              </div>
            )}
            {error ? (
              <Banner
                kind={error.kind ?? "error"}
                size="l"
                detail={
                  error.sampleId ? (
                    <SampleIdRow id={error.sampleId} subtle={false} />
                  ) : undefined
                }
                action={
                  showScanner && error.retry ? (
                    <RetryScanButton onPress={handleRetry} />
                  ) : undefined
                }
              >
                {error.message}
              </Banner>
            ) : undefined}
            <div className={styles.memberRow}>
              <div className={styles.detailRow}>
                <Typography variant="body-xs" color="subtle">
                  Member
                </Typography>
                <Typography variant="label-m">{fullName}</Typography>
              </div>
              <div className={styles.detailRow}>
                <Typography variant="body-xs" color="subtle">
                  Date of birth
                </Typography>
                <Typography variant="label-m">{dateOfBirthLabel}</Typography>
              </div>
            </div>
            {tubes.length > 0 ? (
              <div className={styles.tubesHeading}>
                {/* Deliberately not "drawn": that is wrong the moment one tube
                    is skipped, which is also why the count beside it drops "to
                    draw" once it starts counting settled tubes. */}
                <Typography variant="title-s">
                  {labStatusEnabled || singleVialConfirmEnabled
                    ? "Tubes"
                    : "Tubes to draw"}
                </Typography>
                <Typography variant="body-s" color="subtle">
                  {headingCount}/{tubes.length}
                </Typography>
              </div>
            ) : undefined}
            {labStatusEnabled && labProgress ? (
              <Typography variant="body-s" color="subtle">
                {LAB_PROGRESS_COPY[labProgress]}
              </Typography>
            ) : undefined}
            {tubes.map((tube, index) => {
              const locked = isLocked(tube);

              return (
                <TubeCard
                  key={tube.bloodSampleId}
                  tube={tube}
                  index={index}
                  revealed={isRevealed(tube)}
                  canStart={manualMode}
                  onStart={() => startTube(tube.bloodSampleId)}
                  memberName={legalName}
                  dateOfBirthLabel={dateOfBirthLabel}
                  scanned={scannedIds[tube.bloodSampleId] === true}
                  locked={locked}
                  lockedResolution={locked ? resolutionFor(tube) : undefined}
                  pending={pendingDecisions[tube.bloodSampleId]}
                  labelAttached={labelAttachedIds[tube.bloodSampleId] === true}
                  isSubmitting={confirmDrawMutation.isPending}
                  onToggleConfirm={(checked) =>
                    toggleConfirm(tube.bloodSampleId, checked)
                  }
                  onToggleLabelAttached={(checked) =>
                    toggleLabelAttached(tube.bloodSampleId, checked)
                  }
                  onSkip={() => openSkip(tube)}
                  onUndo={() => undoTube(tube.bloodSampleId)}
                  labStatusEnabled={labStatusEnabled}
                />
              );
            })}
          </section>
        }
        buttons={
          <div className={styles.doneSection}>
            {/* Explains a Done button that can't be pressed. Review state has
                no dead button to explain and can't change back, so it takes
                nothing and Back gets the whole footer. */}
            {nothingLeftToRecord && !reviewMode ? (
              <div className={styles.doneNote}>
                <Typography variant="body-s" color="subtle">
                  Every tube for this member was recorded earlier in this visit.
                  Nothing to do here.
                </Typography>
              </div>
            ) : undefined}
            <ActionsFooter
              left={
                <Button
                  variant="secondary"
                  state={confirmDrawMutation.isPending ? "disabled" : undefined}
                  onPress={exit.onPress}
                  fillContainer
                >
                  {exit.label}
                </Button>
              }
              // Removed, not disabled: a button that can never be pressed
              // reads as something the operator failed to satisfy.
              right={
                reviewMode ? undefined : (
                  <Button
                    state={
                      (!singleVialConfirmEnabled && !allResolved) ||
                      !hasDecisionsToSubmit ||
                      confirmDrawMutation.isPending
                        ? "disabled"
                        : undefined
                    }
                    onPress={handleDone}
                    fillContainer
                  >
                    {doneLabel}
                  </Button>
                )
              }
            />
          </div>
        }
      />

      <BottomSheet
        isOpen={skipTarget != null}
        onClose={closeSkip}
        showCloseIcon={false}
      >
        <div className={styles.skipSheet}>
          <div className={styles.skipSheetContent}>
            <div className={styles.skipSheetHeader}>
              <Typography variant="display-s">Add skip reason</Typography>
              <IconButton
                aria-label="Close"
                variant="tertiary"
                onPress={closeSkip}
              >
                <Icon16Close />
              </IconButton>
            </div>
            <div className={styles.skipSheetDescription}>
              <Typography variant="body-m" color="subtle">
                This will be logged. No draw event will be recorded for this
                tube.
              </Typography>
            </div>
            <RadioGroup
              aria-label="Reason the tube wasn't drawn"
              value={skipReason}
              onChange={(value) =>
                setSkipReason(value as APITypesV1.SkipReason)
              }
            >
              {SKIP_REASONS.map((reason) => (
                <RadioGroup.Radio
                  key={reason}
                  value={reason}
                  label={SKIP_REASON_LABELS[reason]}
                />
              ))}
            </RadioGroup>
          </div>
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={closeSkip} fillContainer>
                Close
              </Button>
            }
            right={
              <Button
                state={!skipReason ? "disabled" : undefined}
                onPress={confirmSkip}
                fillContainer
              >
                Confirm skip
              </Button>
            }
          />
        </div>
      </BottomSheet>
    </PageFrameContent>
  );
}

// A rejection is still a drawn tube, so the brand checkmark would read as
// "all good" on the one case worth acting on.
const RECEIPT_BADGE: Record<
  "drawn" | "skipped" | "rejected",
  { className: string; Icon: ComponentType }
> = {
  drawn: { className: "", Icon: Icon24Checkmark },
  skipped: { className: styles.receiptBadgeNeutral, Icon: Icon24Close },
  rejected: { className: styles.receiptBadgeDanger, Icon: Icon24Close },
};

function SampleIdRow({ id, subtle = true }: { id: string; subtle?: boolean }) {
  return (
    <div className={styles.sampleIdRow}>
      <Typography variant="label-s" color={subtle ? "subtle" : undefined}>
        Sample ID
      </Typography>
      <Typography variant="body-s">{id}</Typography>
    </div>
  );
}

function TubeCard({
  tube,
  index,
  memberName,
  dateOfBirthLabel,
  revealed,
  canStart,
  onStart,
  scanned,
  locked,
  lockedResolution,
  pending,
  labelAttached,
  isSubmitting,
  onToggleConfirm,
  onToggleLabelAttached,
  onSkip,
  onUndo,
  labStatusEnabled,
}: {
  tube: APITypesV1.BloodSample;
  index: number;
  memberName: string | undefined;
  dateOfBirthLabel: string | undefined;
  revealed: boolean;
  canStart: boolean;
  onStart(): void;
  scanned: boolean;
  locked: boolean;
  lockedResolution: TubeResolution | undefined;
  pending: PendingDecision | undefined;
  labelAttached: boolean;
  isSubmitting: boolean;
  onToggleConfirm(checked: boolean): void;
  onToggleLabelAttached(checked: boolean): void;
  onSkip(): void;
  onUndo(): void;
  labStatusEnabled: boolean;
}) {
  const typeLabel = sampleTypeLabels[tube.sampleType];
  // Both values or neither: a half-spelled sentence reads as a rendering fault.
  const identity =
    memberName != null && dateOfBirthLabel != null
      ? { memberName, dateOfBirthLabel }
      : undefined;
  const capColour = tubeCapColour(tube.sampleType);
  const confirmLabel = `${index + 1}. Confirm ${typeLabel} tube`;
  // Names the cap colour, so the checkbox carries it for a screen reader the
  // way the visible sentence carries it for everyone else.
  const labelAttachedLabel = `${index + 1}. Confirm ${typeLabel} label attached to the ${capColour} cap tube`;
  const isDrawnSelected = pending?.decision === "drawn";
  const isSkipped =
    pending?.decision === "skipped" ||
    (locked && lockedResolution === "skipped");
  // Brand border on every tube in play, matching the member details block
  // above. Not started and skipped stay grey: neither is being worked on.
  const isHighlighted = revealed && !isSkipped;
  // Binds the tube in hand to the member being recorded against it, and carries
  // the skip action, so every card still in play gets one.
  const showHeader = !isSkipped && !locked;
  // Nothing left to do to this tube, either way: the card folds down to a
  // one-line receipt and Undo is the only way back in.
  const isConfirmed = isDrawnSelected && labelAttached;
  const isResolved = revealed && (locked || isConfirmed || isSkipped);
  const isOpen = revealed && !isResolved;
  // A tube with a lab status is always locked, so the pill and Undo can never
  // both apply and can share the one slot.
  const labPill = labStatusEnabled ? labPillFor(tube) : undefined;
  const rejected = labStatusEnabled && isRejected(tube);
  const receipt =
    RECEIPT_BADGE[rejected ? "rejected" : isSkipped ? "skipped" : "drawn"];

  return (
    <Group highlight={rejected ? "red" : isHighlighted ? "blue" : "none"}>
      {/* The sample ID shows before the scan so the operator can find the tube. */}
      <div
        className={`${styles.collapse} ${styles.cardCollapse}`}
        data-open={!revealed}
        aria-hidden={revealed}
      >
        <div className={styles.collapseInner}>
          <div className={styles.receiptRow}>
            <div
              className={`${styles.receiptBadge} ${styles.receiptBadgeNeutral}`}
            >
              <Icon24Minus />
            </div>
            <div className={styles.receiptBody}>
              <div className={styles.tubeMeta}>
                <Typography variant="label-m">{typeLabel}</Typography>
                <SampleIdRow id={tube.bloodSampleId} />
              </div>
              <div className={styles.confirmActionsRow}>
                <Typography variant="body-s" color="subtle">
                  Not started
                </Typography>
                {canStart ? (
                  <Link
                    onPress={onStart}
                    isDisabled={isSubmitting}
                    className={styles.secondaryLink}
                  >
                    Draw tube
                  </Link>
                ) : undefined}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`${styles.collapse} ${styles.cardCollapse}`}
        data-open={isOpen}
        aria-hidden={!isOpen}
      >
        <div className={styles.collapseInner}>
          {showHeader ? (
            <div className={styles.scanHeader}>
              {scanned ? <Icon16Capture /> : <Icon16Edit />}
              <Typography variant="label-s" color="inverted">
                {scanned ? "Scanned" : "Manual"} – now recording data for:
              </Typography>
              <Link
                onPress={onSkip}
                isDisabled={isSubmitting}
                className={`${styles.secondaryLink} ${styles.skipTubeLink}`}
              >
                Skip tube
              </Link>
            </div>
          ) : undefined}
          <div className={styles.tubeCard}>
            <div className={styles.tubeMeta}>
              <Typography variant="label-m">{typeLabel}</Typography>
              <SampleIdRow id={tube.bloodSampleId} />
            </div>

            {/* A tube from a prior session is read-only and folds straight to
                its receipt, so only the editable case renders here. */}
            {locked ? undefined : (
              <div className={styles.confirmRows}>
                {/* Checkbox shares the statement's row so it sits against the
                    first line, not the centre of both. */}
                <div className={styles.identityBlock}>
                  <div className={styles.confirmActionsRow}>
                    <Typography variant="body-m">
                      {identity ? IDENTITY_COPY : IDENTITY_COPY_WITHOUT_VALUES}
                    </Typography>
                    <ConfirmToggle
                      ariaLabel={confirmLabel}
                      isSelected={isDrawnSelected}
                      isDisabled={isSubmitting}
                      onToggle={onToggleConfirm}
                    />
                  </div>
                  {identity ? (
                    <div className={styles.identityValues}>
                      <Typography variant="body-m">
                        <strong className={styles.emphasis}>
                          {identity.memberName}
                        </strong>
                        ,{" "}
                        <strong className={styles.emphasis}>
                          {identity.dateOfBirthLabel}
                        </strong>
                      </Typography>
                    </div>
                  ) : undefined}
                </div>
                <div className={styles.confirmActionsRow}>
                  <Typography variant="body-m">
                    I have attached the label to the{" "}
                    <TubeCap capColour={capColour} />{" "}
                    <strong className={styles.emphasis}>{capColour}</strong> cap
                    tube
                  </Typography>
                  <ConfirmToggle
                    ariaLabel={labelAttachedLabel}
                    isSelected={labelAttached}
                    isDisabled={isSubmitting}
                    onToggle={onToggleLabelAttached}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={`${styles.collapse} ${styles.cardCollapse}`}
        data-open={isResolved}
        aria-hidden={!isResolved}
      >
        <div className={styles.collapseInner}>
          <div className={styles.receiptRow}>
            <div className={`${styles.receiptBadge} ${receipt.className}`}>
              <receipt.Icon />
            </div>
            <div className={styles.receiptBody}>
              <div className={styles.tubeMeta}>
                <Typography variant="label-m">{typeLabel}</Typography>
                <SampleIdRow id={tube.bloodSampleId} />
              </div>
              <div className={styles.confirmActionsRow}>
                <Typography
                  variant="body-s"
                  color={isSkipped ? "subtle" : "info"}
                >
                  {isSkipped ? "Skipped" : "Drawn"}
                </Typography>
                {labPill ? (
                  <div className={styles.labPill}>
                    <StatusBadge
                      label={labPill.label}
                      highlight={labPill.highlight}
                      pending={labPill.pending}
                    />
                  </div>
                ) : locked ? undefined : (
                  /* A prior session's decision can't be reversed here. */
                  <Link
                    onPress={onUndo}
                    isDisabled={isSubmitting}
                    className={styles.secondaryLink}
                  >
                    Undo
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Group>
  );
}

function ConfirmToggle({
  ariaLabel,
  isSelected,
  isDisabled,
  onToggle,
}: {
  ariaLabel: string;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle(checked: boolean): void;
}) {
  return (
    <Checkbox
      aria-label={ariaLabel}
      isSelected={isSelected}
      isDisabled={isDisabled}
      onChange={onToggle}
      className={[
        styles.confirmToggle,
        isSelected ? styles.confirmToggleChecked : "",
      ].join(" ")}
    >
      <Icon24Checkmark />
    </Checkbox>
  );
}
